/** Campaign handoff from ampersand.ai. Kept in tab storage through Clerk OAuth. */
const KEY = 'amp_signup_attribution_v1';
const MAX_FRAGMENT = 1800;
const MAX_AGE = 24 * 60 * 60 * 1000;
const TOUCH_AGE = 90 * 24 * 60 * 60 * 1000;
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const;
type Touch = { ts: number } & Partial<Record<(typeof UTM_KEYS)[number], string>>;
type Handoff = { v: 1; at: number; consent: 'accepted' | 'rejected' | 'unknown'; first: Touch | null; last: Touch | null };

function validTouch(value: unknown, now: number): Touch | null {
  if (value === null) return null;
  if (!value || typeof value !== 'object' || Array.isArray(value)) throw new Error('invalid touch');
  const record = value as Record<string, unknown>;
  if (typeof record.ts !== 'number' || !Number.isFinite(record.ts) ||
      record.ts > now + 60_000 || now - record.ts > TOUCH_AGE) throw new Error('invalid timestamp');
  if (Object.keys(record).some(key => key !== 'ts' && !UTM_KEYS.includes(key as typeof UTM_KEYS[number]))) {
    throw new Error('invalid field');
  }
  const touch: Touch = { ts: record.ts };
  for (const key of UTM_KEYS) {
    const field = record[key];
    if (field !== undefined) {
      if (typeof field !== 'string' || !field || field.length > 200) throw new Error('invalid value');
      touch[key] = field;
    }
  }
  if (Object.keys(touch).length < 2) throw new Error('empty touch');
  return touch;
}

function parse(raw: string): Handoff | null {
  if (!raw || raw.length > MAX_FRAGMENT) return null;
  try {
    const value = JSON.parse(decodeURIComponent(raw)) as Record<string, unknown>;
    const now = Date.now();
    if (value.v !== 1 ||
        !['accepted', 'rejected', 'unknown'].includes(value.consent as string) ||
        typeof value.at !== 'number' ||
        !Number.isFinite(value.at) || value.at > now + 60_000 || now - value.at > MAX_AGE ||
        Object.keys(value).some(key => !['v', 'at', 'consent', 'first', 'last'].includes(key))) return null;
    const first = validTouch(value.first, now);
    const last = validTouch(value.last, now);
    if (value.consent !== 'accepted' && (first || last)) return null;
    return { v: 1, at: value.at, consent: value.consent as Handoff['consent'], first, last };
  } catch { return null; }
}

/** Strip the handoff before React, Clerk, PostHog, or navigation reads the URL. */
export function consumeSignupAttribution(): void {
  const hash = window.location.hash;
  const match = hash.match(/(?:^#|&)amp_at=([^&]*)/);
  if (!match) return;
  const cleanHash = hash.replace(/(?:^#|&)amp_at=[^&]*/, '').replace(/^&/, '#');
  window.history.replaceState(window.history.state, '', `${window.location.pathname}${window.location.search}${cleanHash}`);
  if (!/^\/sign-up\/?$/.test(window.location.pathname)) return;
  const incoming = parse(match[1]);
  if (!incoming) return;
  try {
    if (incoming.consent !== 'accepted') {
      sessionStorage.removeItem(KEY);
      return;
    }
    const existing = parse(sessionStorage.getItem(KEY) ?? '');
    sessionStorage.setItem(KEY, encodeURIComponent(JSON.stringify({ ...incoming,
      first: existing?.first && (!incoming.first || existing.first.ts < incoming.first.ts) ? existing.first : incoming.first,
      last: existing?.last && (!incoming.last || existing.last.ts > incoming.last.ts) ? existing.last : incoming.last,
    })));
  } catch { /* blocked storage: signup still works without campaign data */ }
}

export function signupAttributionProperties(): Record<string, string> {
  let handoff: Handoff | null = null;
  try { handoff = parse(sessionStorage.getItem(KEY) ?? ''); } catch { return {}; }
  if (!handoff || handoff.consent !== 'accepted') return {};
  const properties: Record<string, string> = {};
  for (const [prefix, touch] of [['first_touch', handoff.first], ['last_touch', handoff.last]] as const) {
    if (!touch) continue;
    properties[`${prefix}_ts`] = new Date(touch.ts).toISOString();
    for (const key of UTM_KEYS) if (touch[key]) properties[`${prefix}_${key}`] = touch[key];
  }
  return properties;
}
